import React, { useCallback, useEffect, useState } from 'react'
import { Box, Tabs, Tab, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import MosSmartTable from '../common/MosSmartTable'
import CenteredText from '../common/CenteredText'
import useExternalJobTrips, { PENDING_DISPATCH_RESOURCE, DISPATCHED_RESOURCE } from './hooks/useExternalJobTrips'
import useReturnedExternalJobTrips, { DEFAULT_PAGE_SIZE } from './hooks/useReturnedExternalJobTrips'
import useExternalJobColumns from './hooks/useExternalJobColumns'
import ReturnedJobsHistory from './components/ReturnedJobsHistory'
import ExternalJobDateConfirmModal from './components/ExternalJobDateConfirmModal'
import { defaultFromDate } from './utils/returnedPeriod'

const TAB_INDEX = { PENDING_DISPATCH: 0, DISPATCHED: 1, RETURNED: 2 }

/**
 * Story 100 (S98-S105, RN33/RN37/RN39/RN46/RN47, DN-6/DN-7/DN-16/DN-18): the External jobs widget --
 * the ONLY MOS surface where an external (subcontracted) JOB with a declared return deadline is
 * dispatched and returned, and where its deviation against the promised deadline is shown.
 *
 * REDESIGN (2026-09-02, plant feedback): the three populations are now THREE TABS, each backed by
 * `MosSmartTable` (SmartMateriaUITable) -- pending-dispatch and dispatched share one column set
 * (Sun's `ExternalJobTripRowDto`); returned has its OWN dedicated columns, matching Sun's actual,
 * leaner `/returned` contract (`ReturnedTripDto`, PLANT BUG FIX -- see useReturnedJobColumns.js).
 * Does NOT render its own icon+title header: `ExternalJobsWidgetProvider` does not override
 * `hideHeader`, so `WidgetBase` already draws it once -- exactly like every other simple MOS widget
 * (MachineMaterialWidget, ReportablePartsWidget). A second, internal header here was the plant's
 * duplicate-title bug.
 *
 * S98/DN-16: requires a device in context to render its content at all -- belt-and-suspenders on
 * top of the catalog's own `availableWithoutDevice: false` gate (which stops it from ever being
 * ADDED without one): if this widget is ever mounted with no device in context, it shows nothing.
 */
const ExternalJobsWidget = (props = {}) => {
  const { preview = false } = props
  const { t_ } = useSmartTranslation()
  const { device } = useMosInfo()
  const [tabIndex, setTabIndex] = useState(TAB_INDEX.PENDING_DISPATCH)
  // Bumped after a successful dispatch/return so the CURRENTLY VISIBLE tab's table reloads
  // immediately (same "forceLoad bump" pattern as PlannedJobListWidget/MachineMaterialWidget) --
  // only the active tab is ever mounted, and it is always the one the action was triggered from
  // (Dispatch lives on the pending tab, Return on the dispatched tab).
  const [reloadFlag, setReloadFlag] = useState(0)

  // S29-S33 (RN19/RN20/RN23/POL3): onDispatch/onReturn open the SAME date-confirmation modal. The
  // return's own OK/NOK report step lives INSIDE the modal (RN18/NR6: one atomic POST).
  const [dateModal, setDateModal] = useState({ open: false, mode: 'dispatch', row: {} })
  const closeDateModal = () => setDateModal((prev) => ({ ...prev, open: false }))
  const columns = useExternalJobColumns({
    onDispatch: (row) => setDateModal({ open: true, mode: 'dispatch', row }),
    onReturn: (row) => setDateModal({ open: true, mode: 'return', row })
  })

  // S105/CU3-A1: a lightweight, SEPARATE probe (independent of whichever tab's own MosSmartTable
  // pagination is showing) just to know whether the installation has ANY external job of the
  // circuit at all, so the whole widget can degrade to a single empty state instead of three empty
  // tabs. Reuses the pre-existing hooks (unchanged, still filter-free/page-size only, S148) purely
  // for their totals -- never for display.
  const { pendingDispatch: probePending, dispatched: probeDispatched, reload: reloadProbe } = useExternalJobTrips()
  const { totalElements: probeReturnedTotal, reload: reloadReturnedProbe } = useReturnedExternalJobTrips()
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)

  useEffect(() => {
    if (preview || !device?.id) return
    Promise.all([
      reloadProbe(),
      reloadReturnedProbe({ from: defaultFromDate().toISOString(), page: 0, size: DEFAULT_PAGE_SIZE })
    ]).finally(() => setHasLoadedOnce(true))
  }, [preview, device?.id, reloadProbe, reloadReturnedProbe])

  // SAT #3089/#3971: the table's own request travels VERBATIM -- page/size AND its column filters and
  // order (columnFilters/columnFilterValues/columnFilterOperators, sortKey/sortMode). A pageable
  // SmartMateriaUITable never filters its page itself, so keeping only page/size here silently
  // disabled every search box of both tabs. Sun applies them; the client still filters nothing (S148).
  const fetchPendingDispatch = useCallback((pagination) => apiGet(PENDING_DISPATCH_RESOURCE, pagination), [])
  const fetchDispatched = useCallback((pagination) => apiGet(DISPATCHED_RESOURCE, pagination), [])

  if (!preview && !device?.id) {
    return null
  }

  if (preview) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant='body2' color='text.secondary'>{t_('External jobs')}</Typography>
      </Box>
    )
  }

  // S105/CU3-A1: if the installation has no external job of the circuit at all -- nothing pending,
  // nothing dispatched, nothing returned (within the returned population's current period) -- the
  // widget shows a plain, friendly empty state instead of three empty tabs. Deliberately NOT an
  // error: no Alert, no error color, no error icon. Gated on `hasLoadedOnce` so it never flashes
  // before the first fetch resolves.
  const isEmpty = hasLoadedOnce && probePending.length === 0 && probeDispatched.length === 0 && probeReturnedTotal === 0

  if (isEmpty) {
    return (
      <CenteredText>
        <Typography variant='body2' color='text.secondary'>{t_('No external jobs')}</Typography>
      </CenteredText>
    )
  }

  const handleConfirmed = () => {
    closeDateModal()
    setReloadFlag((value) => value + 1)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <ExternalJobDateConfirmModal
        open={dateModal.open}
        mode={dateModal.mode}
        row={dateModal.row}
        deviceId={device?.id}
        onCancel={closeDateModal}
        onConfirmed={handleConfirmed}
      />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={(event, value) => setTabIndex(value)}>
          <Tab label={t_('Pending dispatch')} />
          <Tab label={t_('Dispatched')} />
          <Tab label={t_('Returned')} />
        </Tabs>
      </Box>
      {/* SmartMateriaUITable leaves its TableContainer at overflow: initial (sticky header) and relies on
          its parent to scroll, like ResourceListWidget and PlannedJobListWidget do. */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <div role='tabpanel' hidden={tabIndex !== TAB_INDEX.PENDING_DISPATCH} style={{ height: '100%' }}>
          {tabIndex === TAB_INDEX.PENDING_DISPATCH && (
            <MosSmartTable
              columns={columns}
              dataFetch={fetchPendingDispatch}
              forceLoad={reloadFlag}
              tablePageSize={10}
              pageable
              showSearchHeader
            />
          )}
        </div>
        <div role='tabpanel' hidden={tabIndex !== TAB_INDEX.DISPATCHED} style={{ height: '100%' }}>
          {tabIndex === TAB_INDEX.DISPATCHED && (
            <MosSmartTable
              columns={columns}
              dataFetch={fetchDispatched}
              forceLoad={reloadFlag}
              tablePageSize={10}
              pageable
              showSearchHeader
            />
          )}
        </div>
        <div role='tabpanel' hidden={tabIndex !== TAB_INDEX.RETURNED} style={{ height: '100%' }}>
          {tabIndex === TAB_INDEX.RETURNED && <ReturnedJobsHistory />}
        </div>
      </Box>
    </Box>
  )
}

export default ExternalJobsWidget
